import * as React from "react";
import { Table, TableBody, TableRow, TableCell, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";

const useTableStyles = makeStyles({
    table: {
        width: "100%",
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground1,
    },
    tableBody: {},
    row: {
        display: "flex",
        alignItems: "center",
        width: "100%",
        paddingLeft: tokens.spacingHorizontalXS,
        paddingTop: "8px",
        paddingBottom: "8px",
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        color: tokens.colorNeutralForeground1,
        backgroundColor: tokens.colorNeutralBackground1,
        "&:hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
    },
    cell: {
        paddingLeft: tokens.spacingHorizontalXXS,
        display: "flex",
        alignItems: "center",
    },
    cellFlex: {
        flexGrow: 1,
        flexShrink: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    cellFixed: {
        flexGrow: 0,
        flexShrink: 0,
    },
});

/** Reusable Fluent v9 Table — no header, no selection, theme-aware. */
export interface NewformaTableProps {
    ariaLabel: string;
    children: React.ReactNode;
    className?: string;
}

export const NewformaTable = ({ ariaLabel, children, className }: NewformaTableProps) => {
    const styles = useTableStyles();
    return (
        <Table aria-label={ariaLabel} size="small" className={mergeClasses(styles.table, className)}>
            {children}
        </Table>
    );
};

/** Scrollable table body with max-height constraint. */
export interface NewformaTableBodyProps {
    children: React.ReactNode;
    className?: string;
}

export const NewformaTableBody = ({ children, className }: NewformaTableBodyProps) => {
    const styles = useTableStyles();
    return <TableBody className={mergeClasses(styles.tableBody, className)}>{children}</TableBody>;
};

/** Theme-aware table row with hover effect. */
export interface NewformaTableRowProps {
    children: React.ReactNode;
    className?: string;
}

export const NewformaTableRow = ({ children, className }: NewformaTableRowProps) => {
    const styles = useTableStyles();
    return <TableRow className={mergeClasses(styles.row, className)}>{children}</TableRow>;
};

/** Table cell — use `flex` prop for the flexible column (e.g. attachment name). */
export interface NewformaTableCellProps {
    children: React.ReactNode;
    flex?: boolean;
    className?: string;
    style?: React.CSSProperties;
}

export const NewformaTableCell = ({ children, flex, className, style }: NewformaTableCellProps) => {
    const styles = useTableStyles();
    return (
        <TableCell
            className={mergeClasses(styles.cell, flex ? styles.cellFlex : styles.cellFixed, className)}
            style={style}
        >
            {children}
        </TableCell>
    );
};
